import React, { useState } from 'react';
import { 
  Box, 
  ThemeProvider, 
  createTheme
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CalendarEvent, Listing, CalendarView, EventFormData } from '../../schemas/calendar';
import CalendarHeader from './CalendarHeader';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import EventDialog from './EventDialog';
import { useToast } from '../../hooks/use-toast';

const theme = createTheme({
  palette: {
    primary: {
      main: '#7c3aed',
      light: '#a855f7',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
          boxShadow: '0 4px 20px rgba(124, 58, 237, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
            boxShadow: '0 6px 25px rgba(124, 58, 237, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

interface AdminCalendarProps {
  events: CalendarEvent[];
  listings: Listing[];
}

const AdminCalendar: React.FC<AdminCalendarProps> = ({ events: propEvents, listings }) => {
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [view, setView] = useState<CalendarView>('day');
    const [events, setEvents] = useState<CalendarEvent[]>(propEvents);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | undefined>();
    const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date; listingId?: string } | undefined>();
  
  const { toast } = useToast();

  // Keep events in sync with propEvents
  React.useEffect(() => {
    setEvents(propEvents);
  }, [propEvents]);

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (newView: CalendarView) => {
    setView(newView);
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    switch (view) {
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    switch (view) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsEditing(false);
    setSelectedSlot(undefined);
    setIsEventDialogOpen(true);
  };

  const handleSlotClick = (start: Date, end: Date, listingId?: string) => {
    setSelectedSlot({ start, end, listingId });
    setSelectedEvent(undefined);
    setIsEditing(true);
    setIsEventDialogOpen(true);
  };

  const handleCreateEvent = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 2, 0);
    
    setSelectedSlot({ start, end });
    setSelectedEvent(undefined);
    setIsEditing(true);
    setIsEventDialogOpen(true);
  };

  const handleEditEvent = () => {
    setIsEditing(true);
  };

  const handleSaveEvent = (eventData: EventFormData) => {
    if (selectedEvent) {
      // Update existing event
      const updatedEvents = events.map(event =>{
        // event.id === selectedEvent.id
        //   ? {
        //       ...event,
        //       ...eventData,
        //       id: selectedEvent.id
        //     }
        //   : event
    });
      // setEvents(updatedEvents);
      toast({
        title: "Event Updated",
        description: "The event has been successfully updated.",
      });
    } else {
      // Create new event
      const newEvent: CalendarEvent = {
        ...eventData,
        id: Date.now().toString(),
        customerId: 'new-customer',
        color: listings.find(l => l.id === eventData.listingId)?.color || '#7c3aed'
      };
      setEvents([...events, newEvent]);
      toast({
        title: "Event Created",
        description: "The new event has been successfully created.",
      });
    }
    setIsEventDialogOpen(false);
    setSelectedEvent(undefined);
    setSelectedSlot(undefined);
    setIsEditing(false);
  };

  const handleDeleteEvent = (eventId: string) => {
    const updatedEvents = events.filter(event => event.id !== eventId);
    setEvents(updatedEvents);
    toast({
      title: "Event Deleted",
      description: "The event has been successfully deleted.",
    });
    setIsEventDialogOpen(false);
    setSelectedEvent(undefined);
  };

  const handleCloseDialog = () => {
    setIsEventDialogOpen(false);
    setSelectedEvent(undefined);
    setSelectedSlot(undefined);
    setIsEditing(false);
  };

  const renderCalendarView = () => {
    switch (view) {
      case 'day':
        return (
          <DayView
            currentDate={currentDate}
            events={events}
            listings={listings}
            onEventClick={handleEventClick}
            onSlotClick={handleSlotClick}
          />
        );
      case 'week':
        return (
          <WeekView
            currentDate={currentDate}
            events={events}
            listings={listings}
            onEventClick={handleEventClick}
            onSlotClick={handleSlotClick}
          />
        );
      case 'month':
        return (
          <MonthView
            currentDate={currentDate}
            events={events}
            listings={listings}
            onEventClick={handleEventClick}
            onSlotClick={handleSlotClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            height: { xs: 'calc(100dvh - 180px)', md: 'calc(100vh - 160px)' },
            minHeight: { xs: 620, md: 760 },
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            backgroundColor: 'background.default',
            fontFamily: 'Inter, sans-serif',
            '& *': { boxSizing: 'border-box' },
          }}
        >
          <CalendarHeader
            currentDate={currentDate}
            view={view}
            onDateChange={handleDateChange}
            onViewChange={handleViewChange}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onToday={handleToday}
            onCreateEvent={handleCreateEvent}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              mx: { xs: 0, md: 3 },
              mb: { xs: 1, md: 3 },
              borderRadius: 1,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid',
              borderColor: 'grey.200',
              backgroundColor: 'background.paper'
            }}
          >
            {renderCalendarView()}
          </Box>

          <EventDialog
            open={isEventDialogOpen}
            onClose={handleCloseDialog}
            event={selectedEvent}
            listings={listings}
            isEditing={isEditing}
            onSave={handleSaveEvent}
            onDelete={handleDeleteEvent}
            onEdit={handleEditEvent}
            selectedSlot={selectedSlot}
          />
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default AdminCalendar;